import { importLibrary } from '@googlemaps/js-api-loader';
import { configureGoogleMapsOptions } from './google-maps-loader.js';

const UNAVAILABLE =
    'No pudimos consultar Google. Puedes completar la dirección manualmente.';
const REVIEW =
    'Comprueba la calle, número, código postal y localidad. Añade el piso y puerta antes de guardar o pedir.';

/** Keep editable customer address fields only; never provider objects or coordinates. */
export function parseAddressComponents(components) {
    if (!Array.isArray(components)) return null;
    const component = (type, short = false) => {
        const value = components.find((item) => item.types?.includes(type));
        const text = short ? value?.shortText : value?.longText;
        return typeof text === 'string' ? text.trim() : '';
    };
    if (
        component('country', true).toUpperCase() !== 'ES' ||
        component('route') === ''
    )
        return null;
    const street = [component('route'), component('street_number')]
        .filter(Boolean)
        .join(' ');
    return {
        street: component('subpremise')
            ? `${street}, ${component('subpremise')}`
            : street,
        postalCode: component('postal_code'),
        city:
            component('locality') ||
            component('postal_town') ||
            component('administrative_area_level_3'),
    };
}

async function loadPlaces(browserKey) {
    configureGoogleMapsOptions({
        key: browserKey,
        language: 'es',
        region: 'ES',
        v: 'weekly',
    });
    return importLibrary('places');
}

export function createAddressSelectionController({
    browserKey,
    loadPlaces: importPlaces = loadPlaces,
    attachWidget,
    readAddress,
    applyAddress,
    setStatus,
}) {
    let alive = true;
    let generation = 0;
    let pendingLoad = null;
    let widget = null;

    function edited() {
        generation += 1;
    }

    function unavailable() {
        generation += 1;
        if (alive) setStatus(UNAVAILABLE);
    }

    async function select({ placePrediction }) {
        if (!alive) return;
        const selection = ++generation;
        const original = JSON.stringify(readAddress());
        setStatus('Consultando la dirección seleccionada…');
        try {
            const place = placePrediction.toPlace();
            await place.fetchFields({ fields: ['addressComponents'] });
            if (!alive || selection !== generation) return;
            if (original !== JSON.stringify(readAddress())) {
                setStatus(
                    'Se conservan tus cambios manuales. Puedes volver a buscar si lo necesitas.',
                );
                return;
            }
            const address = parseAddressComponents(place.addressComponents);
            if (address === null) {
                setStatus(UNAVAILABLE);
                return;
            }
            applyAddress(address);
            setStatus(REVIEW);
        } catch {
            if (alive && selection === generation) setStatus(UNAVAILABLE);
        }
    }

    async function start() {
        if (!alive) return;
        if (typeof browserKey !== 'string' || browserKey.trim() === '') {
            setStatus(UNAVAILABLE);
            return;
        }
        if (widget) {
            widget.focus();
            return;
        }
        if (pendingLoad) return pendingLoad;
        setStatus('Cargando búsqueda de direcciones…');
        pendingLoad = (async () => {
            try {
                const { PlaceAutocompleteElement } =
                    await importPlaces(browserKey);
                if (!alive) return;
                widget = new PlaceAutocompleteElement({
                    includedRegionCodes: ['es'],
                    includedPrimaryTypes: [
                        'street_address',
                        'premise',
                        'subpremise',
                    ],
                });
                widget.placeholder = 'Busca tu calle y número';
                widget.addEventListener('gmp-select', select);
                widget.addEventListener('gmp-error', unavailable);
                attachWidget(widget);
                widget.focus();
                setStatus(
                    'Selecciona una sugerencia o completa los campos manualmente.',
                );
            } catch {
                if (alive) setStatus(UNAVAILABLE);
            } finally {
                pendingLoad = null;
            }
        })();
        return pendingLoad;
    }

    function dispose() {
        alive = false;
        generation += 1;
        widget?.removeEventListener('gmp-select', select);
        widget?.removeEventListener('gmp-error', unavailable);
        widget = null;
        attachWidget(null);
    }

    return { start, select, edited, dispose };
}

export function applyCustomerAddress(fields, address, wire = null) {
    const values = [address.street, address.postalCode, address.city];
    fields.forEach((field, index) => {
        field.value = values[index];
        const model = field.getAttribute('wire:model');
        if (wire && model) wire.set(model, values[index], false);
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
    });
}

export function installCustomerAddressAutocomplete({
    documentTarget = document,
} = {}) {
    const controllers = new Map();

    function mount() {
        for (const [root, destroy] of controllers) {
            if (!root.isConnected) {
                destroy();
                controllers.delete(root);
            }
        }
        for (const root of documentTarget.querySelectorAll(
            '[data-address-autocomplete]',
        )) {
            if (controllers.has(root)) continue;
            const scope = root.closest('[data-customer-address-fields]');
            const button = root.querySelector('[data-address-search-button]');
            const host = root.querySelector('[data-address-search-widget]');
            const status = root.querySelector('[data-address-search-status]');
            if (!scope || !button || !host || !status) continue;
            const ids = [
                root.dataset.streetId,
                root.dataset.postalId,
                root.dataset.cityId,
            ];
            const fields = () =>
                ids.map((id) =>
                    [...scope.querySelectorAll('input')].find(
                        (input) => input.id === id,
                    ),
                );
            if (fields().some((field) => !field)) continue;
            const readAddress = () => {
                const current = fields();
                return {
                    street: current[0]?.value,
                    postalCode: current[1]?.value,
                    city: current[2]?.value,
                };
            };
            const controller = createAddressSelectionController({
                browserKey: root.dataset.browserKey,
                attachWidget(widget) {
                    host.replaceChildren(...(widget ? [widget] : []));
                    host.hidden = !widget;
                },
                readAddress,
                applyAddress(address) {
                    const current = fields();
                    if (!root.isConnected || current.some((field) => !field))
                        return;
                    const wireId = scope
                        .closest('[wire\\:id]')
                        ?.getAttribute('wire:id');
                    const wire = wireId
                        ? globalThis.Livewire?.find(wireId)
                        : null;
                    applyCustomerAddress(current, address, wire);
                },
                setStatus(message) {
                    status.textContent = message;
                },
            });
            const start = () => {
                void controller.start();
            };
            const edit = (event) => {
                if (fields().includes(event.target)) controller.edited();
            };
            button.addEventListener('click', start);
            scope.addEventListener('input', edit);
            controllers.set(root, () => {
                button.removeEventListener('click', start);
                scope.removeEventListener('input', edit);
                controller.dispose();
            });
        }
    }

    const observer = new MutationObserver(mount);
    observer.observe(documentTarget.documentElement, {
        childList: true,
        subtree: true,
    });
    documentTarget.addEventListener('livewire:navigated', mount);
    documentTarget.addEventListener('DOMContentLoaded', mount);
    mount();

    return {
        dispose() {
            observer.disconnect();
            documentTarget.removeEventListener('livewire:navigated', mount);
            documentTarget.removeEventListener('DOMContentLoaded', mount);
            for (const destroy of controllers.values()) destroy();
            controllers.clear();
        },
    };
}
